import { Scrim, Surface } from "@/components/ui"
import { useModalLayer } from "@/contexts/ModalContext"
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll"
import * as Dialog from "@radix-ui/react-dialog"
import type { ReactNode } from "react"

type PrizeGameSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onEscapeKeyDown?: (event: KeyboardEvent) => void
  children: ReactNode
}

/**
 * The iOS-style container of the prize game: on phones a bottom sheet with 30px top
 * corners, a grab handle and the bottom safe area; on desktop a centered card
 * rounded all round. Springs up over a dimmed, blurred veil and only fades
 * under reduced motion. Same non-modal Radix setup as `BottomSheet`; it is the
 * game surface (fixed palette, Satoshi body), so text inside takes the game ink.
 */
export function PrizeGameSheet({ open, onOpenChange, onEscapeKeyDown, children }: PrizeGameSheetProps) {
  const { zIndex, backdropZIndex } = useModalLayer(open)
  useLockBodyScroll(open)

  return (
    <Dialog.Root modal={false} open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Scrim zIndex={backdropZIndex} tone="game" className="animate-fade-in" onClick={() => onOpenChange(false)} />
        <Dialog.Content asChild aria-describedby={undefined} onEscapeKeyDown={onEscapeKeyDown}>
          <Surface
            surface="game"
            ink
            style={{ zIndex }}
            className="fixed inset-x-0 bottom-0 max-h-game-sheet overflow-y-auto overscroll-contain rounded-t-game-sheet px-5 pb-safe-lg shadow-game-sheet motion-safe:data-[state=open]:animate-game-sheet-in motion-safe:data-[state=closed]:animate-game-sheet-out motion-reduce:data-[state=open]:animate-fade-in motion-reduce:data-[state=closed]:animate-fade-out md:inset-0 md:m-auto md:h-fit md:max-h-game-dialog md:w-game-dialog md:rounded-game-dialog md:pb-8 md:pt-6 md:shadow-game-dialog md:motion-safe:data-[state=open]:animate-game-card-in md:motion-safe:data-[state=closed]:animate-game-card-out"
          >
            <div aria-hidden className="flex justify-center pb-1 pt-2.5 md:hidden">
              <div className="h-game-grabber w-9 rounded-full bg-game-line" />
            </div>
            {children}
          </Surface>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
